import React, { useState, useEffect, useCallback } from 'react';
import {
    BookOpen, ChevronDown, ChevronRight, ChevronLeft,
    Loader2, AlertCircle, Car, Search,
} from 'lucide-react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
    fetchFleetServiceRecords,
    fetchFleetServiceRecordDetail,
    selectFleetServiceRecordList,
    selectFleetServiceRecordDetailById,
} from '../store/slices/fleetServiceRecordSlice';
import FleetServiceRecordDetail from './FleetServiceRecordDetail';
import Layout from './Layout';

const PAGE_SIZE = 20;

// ============================================
// Date helpers
// ============================================

const iso = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

/** Default window: the last month, ending today. */
const defaultRange = () => {
    const to = new Date();
    const from = new Date();
    from.setMonth(from.getMonth() - 1);
    return { from: iso(from), to: iso(to) };
};

const longDate = (instant) =>
    !instant
        ? '—'
        : new Date(instant).toLocaleDateString('en-AU', {
            day: 'numeric', month: 'short', year: 'numeric',
        });

// ============================================
// Expanded detail — fetches on first expand, then renders the shared component
// ============================================

const RecordDetail = ({ id }) => {
    const dispatch = useAppDispatch();
    const detailById = useAppSelector(selectFleetServiceRecordDetailById);
    const slot = detailById[id] ?? { data: null, loading: false, error: null };

    useEffect(() => {
        if (!slot.data && !slot.loading && !slot.error) {
            dispatch(fetchFleetServiceRecordDetail(id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    if (slot.loading) {
        return (
            <div className="py-8 flex items-center justify-center">
                <Loader2 size={20} className="text-emerald-500 animate-spin" />
            </div>
        );
    }

    if (slot.error) {
        return (
            <div className="py-6 flex items-start gap-2 px-6">
                <AlertCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-600">{slot.error}</p>
            </div>
        );
    }

    return <FleetServiceRecordDetail record={slot.data} />;
};

// ============================================
// Fleet service history page
// ============================================

const FleetServiceRecordsPage = () => {
    const dispatch = useAppDispatch();
    const list = useAppSelector(selectFleetServiceRecordList);

    const [range, setRange] = useState(defaultRange);
    const [expanded, setExpanded] = useState(null);

    const load = useCallback((page = 0, r = range) => {
        dispatch(fetchFleetServiceRecords({
            from: r.from,
            to: r.to,
            page,
            size: PAGE_SIZE,
        }));
    }, [dispatch, range]);

    // Load the default (last month) window on mount — a fleet customer's history
    // is small enough to show immediately, unlike the org-wide workshop search.
    useEffect(() => {
        const r = defaultRange();
        dispatch(fetchFleetServiceRecords({ from: r.from, to: r.to, page: 0, size: PAGE_SIZE }));
    }, [dispatch]);

    const goToPage = (target) => {
        if (target < 0 || target >= list.totalPages) return;
        setExpanded(null);
        load(target);
    };

    const toggle = (id) => setExpanded((cur) => (cur === id ? null : id));

    const inputCls =
        'px-3 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent';

    return (
        <Layout>
            <div className="px-6 py-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-11 h-11 bg-emerald-50 rounded-xl flex items-center justify-center border border-emerald-100">
                        <BookOpen size={22} className="text-emerald-600" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-display font-bold text-gray-900">Service History</h1>
                        <p className="text-sm text-gray-500 mt-0.5">
                            Completed services for every vehicle in your fleet.
                        </p>
                    </div>
                </div>

                {/* Card */}
                <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                    {/* Date range */}
                    <div className="p-4 border-b border-gray-100 flex flex-wrap items-end gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1.5">From</label>
                            <input
                                type="date"
                                value={range.from}
                                onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))}
                                className={inputCls}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-500 mb-1.5">To</label>
                            <input
                                type="date"
                                value={range.to}
                                onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))}
                                className={inputCls}
                            />
                        </div>
                        <button
                            onClick={() => { setExpanded(null); load(0); }}
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors text-sm font-semibold cursor-pointer border-none"
                        >
                            <Search size={16} />
                            Search
                        </button>
                    </div>

                    {list.loading ? (
                        <div className="py-16 flex flex-col items-center justify-center">
                            <Loader2 size={28} className="text-emerald-500 animate-spin" />
                            <p className="mt-3 text-sm text-gray-500">Loading your service history…</p>
                        </div>
                    ) : list.error ? (
                        <div className="py-16 flex flex-col items-center justify-center px-6 text-center">
                            <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-3">
                                <AlertCircle size={22} className="text-red-500" />
                            </div>
                            <p className="text-sm text-gray-900 font-medium">Couldn't load service history</p>
                            <p className="text-sm text-gray-500 mt-1">{list.error}</p>
                            <button
                                onClick={() => load(0)}
                                className="mt-4 px-4 py-2 bg-gray-900 text-white rounded-xl text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer border-none"
                            >
                                Try again
                            </button>
                        </div>
                    ) : list.records.length === 0 ? (
                        <div className="py-16 flex flex-col items-center justify-center px-6 text-center">
                            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                                <BookOpen size={22} className="text-gray-400" />
                            </div>
                            <p className="text-sm text-gray-900 font-medium">No services in this period</p>
                            <p className="text-sm text-gray-500 mt-1">
                                Try widening the date range.
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="divide-y divide-gray-50">
                                {list.records.map((r) => {
                                    const isOpen = expanded === r.id;
                                    return (
                                        <div key={r.id}>
                                            <button
                                                onClick={() => toggle(r.id)}
                                                className="w-full px-6 py-4 flex items-center gap-4 hover:bg-gray-50 transition-colors bg-transparent border-none cursor-pointer text-left"
                                            >
                                                {isOpen
                                                    ? <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />
                                                    : <ChevronRight size={16} className="text-gray-400 flex-shrink-0" />}

                                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-100 rounded-lg text-sm font-semibold text-gray-900">
                                                    <Car size={14} className="text-gray-400" />
                                                    {r.rego}
                                                </span>

                                                <span className="text-sm text-gray-600">
                                                    {longDate(r.serviceCompletionDate)}
                                                </span>

                                                <span className="ml-auto text-xs text-gray-400">
                                                    {isOpen ? 'Hide' : 'View'} details
                                                </span>
                                            </button>

                                            {isOpen && <RecordDetail id={r.id} />}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Pagination */}
                            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
                                <p className="text-sm text-gray-500">
                                    {list.totalElements} {list.totalElements === 1 ? 'service' : 'services'}
                                </p>
                                {list.totalPages > 1 && (
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => goToPage(list.page - 1)}
                                            disabled={list.page <= 0}
                                            className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors bg-transparent cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <ChevronLeft size={16} />
                                        </button>
                                        <span className="text-sm text-gray-600 px-2">
                                            Page {list.page + 1} of {list.totalPages}
                                        </span>
                                        <button
                                            onClick={() => goToPage(list.page + 1)}
                                            disabled={list.page >= list.totalPages - 1}
                                            className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 transition-colors bg-transparent cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default FleetServiceRecordsPage;